// TA-43: geen bare specifier (`@theme/sample-request-state`) gebruiken. Safari < 16.4
// ondersteunt geen import maps en gooit dan "module specifier ... does not start with /".
// We lezen de geversioneerde URL (met ?v=) uit de import map van de theme, zodat de CDN-cache
// klopt en moderne browsers dezelfde module-instantie delen als via `@theme/...`.
function resolveSampleStateUrl() {
  try {
    const map = document.querySelector('script[type="importmap"]');
    const url = map && JSON.parse(map.textContent).imports['@theme/sample-request-state'];
    if (url) return new URL(url, document.baseURI).href;
  } catch (error) {
    // Val terug op het relatieve pad hieronder.
  }
  return new URL('./sample-request-state.js', import.meta.url).href;
}

const {
  addSample,
  clearState,
  getActiveState,
  getCount,
  getDefaultTtlMs,
  hasProduct,
  removeSample,
  setCustomer,
  setRequestId,
  setSettings,
  setStore,
  setSubmittedRequestData,
  updateSample,
} = await import(resolveSampleStateUrl());

const ROOT_SELECTOR = '[data-sample-request-root]';
const MODAL_SELECTOR = '[data-sample-request-modal]';
const QUICK_DIALOG_SELECTOR = '[data-sample-request-quick-dialog]';
const BADGE_SELECTOR = '[data-sample-request-badge]';
const THANK_YOU_SELECTOR = '[data-sample-request-thank-you]';
const STORE_DATA_SELECTOR = '[data-sample-request-store-data]';
const TILES_COLLECTION_URL = '/collections/tegels';
const DEFAULT_SUBMISSION_URL = 'https://tegelmegastore.app.n8n.cloud/webhook/sample-request';
const SUBMISSION_TIMEOUT_MS = 15000;
const IDEMPOTENCY_STORAGE_KEY = 'tms_sample_request_idempotency';
const IDEMPOTENCY_FP_STORAGE_KEY = 'tms_sample_request_idempotency_fp';
const STORE_SEARCH_DEBOUNCE_MS = 300;
const OFFERTE_DEFAULT_WASTAGE = 10;
const STORE_SEARCH_MIN_PLACE_LENGTH = 3;
const PDOK_LOCATIE_URL = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/free';
const PDOK_FETCH_TIMEOUT_MS = 5000;

const STORE_SEARCH_ALIASES = {
  breda: ['breda'],
  'den-bosch': ['den bosch', 'denbosch', 's-hertogenbosch', 'shertogenbosch', 'hertogenbosch'],
  'den-haag': ['den haag', 'denhaag'],
  deventer: ['deventer'],
  tilburg: ['tilburg'],
  zaandam: ['zaandam'],
  nuenen: ['nuenen', 'eindhoven'],
};
const DELETE_ICON = `
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
    <path d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
`;

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const state = {
  stores: [],
  activeStoreId: null,
  provisionalProductUrl: null,
  latestAddedProductUrl: null,
  badgeObserver: null,
  storeSearchRequestId: 0,
  searchGeocodeCache: new Map(),
  storeSearchDebounceTimer: null,
  preventModalClose: false,
  submissionInFlight: false,
  offerteRowsSignature: null,
};

function getMainModal() {
  return document.querySelector(MODAL_SELECTOR);
}

function getQuickDialog() {
  return document.querySelector(QUICK_DIALOG_SELECTOR);
}

function getBadges() {
  return Array.from(document.querySelectorAll(BADGE_SELECTOR));
}

function getStoreById(storeId) {
  return state.stores.find((store) => store.id === storeId) || null;
}

function patchModalCloseGuard(wrapper) {
  if (!wrapper || wrapper.dataset.sampleRequestCloseGuard === 'true') return;

  wrapper.dataset.sampleRequestCloseGuard = 'true';

  const dialog = wrapper.querySelector('dialog');

  if (typeof wrapper.closeDialog === 'function') {
    const nativeCloseDialog = wrapper.closeDialog.bind(wrapper);
    wrapper.closeDialog = async (...args) => {
      if (state.preventModalClose) return;
      return nativeCloseDialog(...args);
    };
  }

  dialog?.addEventListener('cancel', (event) => {
    if (state.preventModalClose) {
      event.preventDefault();
    }
  });
}

function getModalDialogController(wrapper) {
  if (!wrapper) return null;

  patchModalCloseGuard(wrapper);

  if (typeof wrapper.showDialog === 'function' && typeof wrapper.closeDialog === 'function') {
    return wrapper;
  }

  const dialog = wrapper.querySelector('dialog');
  if (!dialog) return null;

  return {
    showDialog() {
      if (!dialog.open) dialog.showModal();
    },
    closeDialog() {
      if (state.preventModalClose) return;
      if (dialog.open) dialog.close();
    },
    get refs() {
      return { dialog };
    },
  };
}

function decodeRemoveSampleUrl(button) {
  const raw = button.getAttribute('data-remove-sample');
  if (!raw) return '';

  try {
    return decodeURIComponent(raw);
  } catch (_error) {
    return raw;
  }
}

function handleSampleRemove(event, ttlMs) {
  const wrapper = getMainModal();
  const removeButton = event.target.closest('[data-remove-sample]');
  if (!removeButton || !wrapper?.contains(removeButton)) return false;

  event.preventDefault();
  event.stopPropagation();

  const productUrl = decodeRemoveSampleUrl(removeButton);
  removeSample(productUrl, { ttlMs });

  if (state.provisionalProductUrl === productUrl) {
    state.provisionalProductUrl = null;
  }

  requestAnimationFrame(() => {
    populateStoreList(ttlMs);
    updateSidebar(ttlMs);

    requestAnimationFrame(() => {
      state.preventModalClose = false;
    });
  });

  return true;
}

function parseStoreLocations() {
  const script = document.querySelector(STORE_DATA_SELECTOR);
  if (!script) return [];

  try {
    return JSON.parse(script.textContent || '[]');
  } catch (_error) {
    return [];
  }
}

function getThemeSubmissionUrl() {
  return getMainModal()?.dataset.submissionUrl?.trim() || '';
}

function normalizeConfigFromDataset(dataset) {
  return {
    maxSamplesTotal: Number.parseInt(dataset.maxSamplesTotal || '3', 10) || 3,
    submissionUrl: dataset.submissionUrl?.trim() || getThemeSubmissionUrl() || DEFAULT_SUBMISSION_URL,
    thankYouUrl: dataset.thankYouUrl || '/pages/bedankt-voor-je-sample-aanvraag',
    privacyUrl: dataset.privacyUrl || '/policies/privacy-policy',
  };
}

function getConfig(ttlMs) {
  const activeState = getActiveState(ttlMs);
  const fallbackSubmissionUrl = getThemeSubmissionUrl() || DEFAULT_SUBMISSION_URL;

  return {
    maxSamplesTotal: activeState?.settings?.maxSamplesTotal || 3,
    submissionUrl: activeState?.settings?.submissionUrl || fallbackSubmissionUrl,
    thankYouUrl: activeState?.settings?.thankYouUrl || '/pages/bedankt-voor-je-sample-aanvraag',
    privacyUrl: activeState?.settings?.privacyUrl || '/policies/privacy-policy',
  };
}

function renderSampleItems(items, { allowRemove = true, maxSamplesTotal = 3, showAddMore = true } = {}) {
  const remainingSlots = Math.max(maxSamplesTotal - items.length, 0);
  const addMoreItemMarkup =
    showAddMore && remainingSlots > 0
      ? `
        <li>
          <a href="${TILES_COLLECTION_URL}" class="sample-request__add-more" data-sample-request-add-more>
            <span class="sample-request__add-more-icon" aria-hidden="true">+</span>
            <span class="sample-request__add-more-content">
              <span class="sample-request__add-more-title">Meer samples toevoegen</span>
              <span class="sample-request__add-more-text">Je kunt nog ${remainingSlots} ${remainingSlots === 1 ? 'sample' : 'samples'} toevoegen</span>
            </span>
          </a>
        </li>
      `
      : '';

  if (!items.length) {
    return `<li class="sample-request__empty">Nog geen samples toegevoegd.</li>${addMoreItemMarkup}`;
  }

  return `${items
    .map((sample) => {
      const imageMarkup = sample.imageUrl
        ? `<img src="${sample.imageUrl}" alt="${sample.productTitle}" loading="lazy">`
        : '<span class="sample-request__sample-thumb-placeholder"></span>';

      const contentInnerMarkup = `
        <p class="sample-request__sample-type">${sample.productType || ''}</p>
        <p class="sample-request__sample-title">${sample.productTitle}</p>
      `;

      const contentMarkup = sample.productUrl
        ? `<a class="sample-request__sample-link" href="${sample.productUrl}">${contentInnerMarkup}</a>`
        : `<div class="sample-request__sample-link">${contentInnerMarkup}</div>`;

      const removeButton = allowRemove
        ? `<button type="button" class="sample-request__sample-remove" data-remove-sample="${encodeURIComponent(sample.productUrl)}" aria-label="Verwijder ${sample.productTitle}"><span class="sample-request__sample-remove-label">Verwijderen</span><span class="sample-request__sample-remove-icon">${DELETE_ICON}</span></button>`
        : '';

      return `
        <li class="sample-request__sample-item">
          <div class="sample-request__sample-thumb">${imageMarkup}</div>
          <div class="sample-request__sample-content">${contentMarkup}</div>
          ${removeButton}
        </li>
      `;
    })
    .join('')}${addMoreItemMarkup}`;
}

function getSummaryStore(ttlMs) {
  const activeState = getActiveState(ttlMs);
  if (state.activeStoreId) {
    return getStoreById(state.activeStoreId);
  }

  if (activeState?.store?.id) {
    return getStoreById(activeState.store.id) || activeState.store;
  }

  return state.stores[0] || null;
}

function updateBadge(ttlMs) {
  const count = getCount(ttlMs);
  const badges = getBadges();
  if (!badges.length) return;

  badges.forEach((badge) => {
    badge.hidden = count === 0;
    badge.setAttribute('aria-hidden', count === 0 ? 'true' : 'false');

    const countEls = badge.querySelectorAll('[data-sample-request-count]');
    countEls.forEach((el) => {
      el.textContent = String(count);
    });
  });
}

function updateRoots(ttlMs) {
  const count = getCount(ttlMs);

  document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
    const productUrl = root.dataset.productUrl;
    const button = root.querySelector('[data-sample-request-trigger]');
    const buttonText = button?.querySelector('[data-sample-request-button-text]');
    const errorEl = root.querySelector('[data-sample-request-error]');
    const config = normalizeConfigFromDataset(root.dataset);

    if (!button || !buttonText) return;

    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    if (hasProduct(productUrl, ttlMs)) {
      button.disabled = true;
      button.classList.add('sample-request-button--disabled');
      buttonText.textContent = root.dataset.labelAlreadyRequested;
      return;
    }

    if (count >= config.maxSamplesTotal) {
      button.disabled = true;
      button.classList.add('sample-request-button--disabled');
      buttonText.textContent = (root.dataset.labelMaxReached || '').replace('[count]', String(count));
      return;
    }

    button.disabled = false;
    button.classList.remove('sample-request-button--disabled');
    buttonText.textContent = root.dataset.labelDefault;
  });
}

function normalizeSearchText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['.]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function getStoreSearchHaystack(store) {
  const parts = [store.city, store.name, store.postalCode, store.street, ...(STORE_SEARCH_ALIASES[store.id] || [])];
  return normalizeSearchText(parts.join(' '));
}

function compactSearchTerm(term) {
  return normalizeSearchText(term).replace(/\s+/g, '');
}

function getStorePostcodeDigits(store) {
  return normalizeSearchText(store.postalCode).replace(/\s+/g, '').slice(0, 4);
}

function getPostcodeDigits(term) {
  const compact = compactSearchTerm(term);
  const match = compact.match(/^(\d{1,4})([a-z]{0,2})$/i);
  if (!match) return null;
  return { prefix: match[1], suffix: match[2] || '' };
}

function filterStoresByPostcode(stores, term) {
  const postcodeQuery = getPostcodeDigits(term);
  if (!postcodeQuery) return [];

  const { prefix, suffix } = postcodeQuery;

  return stores.filter((store) => {
    const storeDigits = getStorePostcodeDigits(store);
    const storeSuffix = normalizeSearchText(store.postalCode).replace(/\s+/g, '').slice(4, 6);

    if (!storeDigits.startsWith(prefix)) return false;
    if (suffix && storeSuffix !== suffix.toLowerCase()) return false;

    return true;
  });
}

function filterStoresByPlaceName(stores, term) {
  const normalizedTerm = normalizeSearchText(term);
  if (!normalizedTerm || /\d/.test(normalizedTerm)) return [];

  return stores.filter((store) => {
    const haystack = getStoreSearchHaystack(store);
    return haystack.includes(normalizedTerm);
  });
}

function getLocalStoreMatches(stores, term) {
  const trimmed = term.trim();
  if (!trimmed) return stores;

  const postcodeMatches = filterStoresByPostcode(stores, trimmed);
  if (postcodeMatches.length) {
    return postcodeMatches;
  }

  return filterStoresByPlaceName(stores, trimmed);
}

function shouldGeocodeSearch(term) {
  const trimmed = term.trim();
  if (trimmed.length < STORE_SEARCH_MIN_PLACE_LENGTH) return false;

  const postcodeQuery = getPostcodeDigits(trimmed);
  if (postcodeQuery?.prefix.length === 4) {
    return true;
  }

  const hasLetters = /[a-z]/i.test(trimmed);
  return hasLetters && normalizeSearchText(trimmed).length >= STORE_SEARCH_MIN_PLACE_LENGTH;
}

function getStoreCoordinates(store) {
  const latitude = Number.parseFloat(store.latitude);
  const longitude = Number.parseFloat(store.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { lat: latitude, lng: longitude };
}

function haversineDistanceKm(from, to) {
  const earthRadiusKm = 6371;
  const latDelta = ((to.lat - from.lat) * Math.PI) / 180;
  const lngDelta = ((to.lng - from.lng) * Math.PI) / 180;
  const fromLat = (from.lat * Math.PI) / 180;
  const toLat = (to.lat * Math.PI) / 180;

  const a =
    Math.sin(latDelta / 2) * Math.sin(latDelta / 2) +
    Math.cos(fromLat) * Math.cos(toLat) * Math.sin(lngDelta / 2) * Math.sin(lngDelta / 2);

  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function sortStoresByDistance(stores, origin) {
  return [...stores].sort((left, right) => {
    const leftCoords = getStoreCoordinates(left);
    const rightCoords = getStoreCoordinates(right);

    if (!leftCoords && !rightCoords) return 0;
    if (!leftCoords) return 1;
    if (!rightCoords) return -1;

    return (
      haversineDistanceKm(origin, { lat: leftCoords.lat, lng: leftCoords.lng }) -
      haversineDistanceKm(origin, { lat: rightCoords.lat, lng: rightCoords.lng })
    );
  });
}

function parsePdokCentroid(centroide) {
  if (!centroide) return null;

  const pointMatch = /^POINT\(([-\d.]+)\s+([-\d.]+)\)$/i.exec(String(centroide).trim());
  if (pointMatch) {
    const lng = Number.parseFloat(pointMatch[1]);
    const lat = Number.parseFloat(pointMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng };
    }
  }

  const commaParts = String(centroide)
    .split(',')
    .map((part) => Number.parseFloat(part.trim()));
  if (commaParts.length === 2 && commaParts.every(Number.isFinite)) {
    return { lat: commaParts[0], lng: commaParts[1] };
  }

  return null;
}

async function resolveSearchOrigin(term, localMatches) {
  if (shouldGeocodeSearch(term)) {
    const geocoded = await geocodeDutchLocation(term);
    if (geocoded) return geocoded;
  }

  if (localMatches.length) {
    return getStoreCoordinates(localMatches[0]);
  }

  return null;
}

async function fetchPdokCoords(
  query,
  { postcodeType = false, expectedPrefix = null, timeoutMs = PDOK_FETCH_TIMEOUT_MS } = {}
) {
  const url = new URL(PDOK_LOCATIE_URL);
  url.searchParams.set('q', query);
  url.searchParams.set('rows', '1');

  if (postcodeType) {
    url.searchParams.set('fq', 'type:postcode');
  } else {
    url.searchParams.set('fq', 'type:woonplaats');
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    if (!response.ok) return null;

    const data = await response.json();
    const doc = data?.response?.docs?.[0];
    const coords = parsePdokCentroid(doc?.centroide_ll);
    if (!coords) return null;

    if (expectedPrefix) {
      const docPostcode = String(doc?.postcode || '').replace(/\s+/g, '').toLowerCase();
      if (docPostcode && !docPostcode.startsWith(expectedPrefix)) {
        return null;
      }
    }

    return coords;
  } catch (_error) {
    return null;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function geocodePostcode4Prefix(prefix) {
  const directCoords = await fetchPdokCoords(prefix, { postcodeType: true, expectedPrefix: prefix });
  if (directCoords) return directCoords;

  const letterSuffixes = ['AB', 'AC', 'AD', 'AA', 'AE', 'BB', 'BC', 'BD', 'BE', 'CD', 'DE'];
  for (const suffix of letterSuffixes) {
    const coords = await fetchPdokCoords(`${prefix}${suffix}`, { postcodeType: true, expectedPrefix: prefix });
    if (coords) return coords;
  }

  const base = Number.parseInt(prefix, 10);
  const offsets = [11, 1, 10, 20, 50, 2, 5, 100, -1, -11, -10];

  for (const offset of offsets) {
    const candidate = String(base + offset).padStart(4, '0');
    if (candidate.length !== 4 || candidate === prefix) continue;

    const coords = await fetchPdokCoords(`${candidate}AB`, { postcodeType: true });
    if (coords) return coords;
  }

  return null;
}

async function geocodeDutchLocation(query) {
  const normalizedQuery = query.trim();
  if (!shouldGeocodeSearch(normalizedQuery)) return null;

  const cacheKey = normalizeSearchText(normalizedQuery);
  if (state.searchGeocodeCache.has(cacheKey)) {
    return state.searchGeocodeCache.get(cacheKey);
  }

  const postcodeQuery = getPostcodeDigits(normalizedQuery);

  try {
    let coords = null;

    if (postcodeQuery?.prefix.length === 4 && !postcodeQuery.suffix) {
      coords = await geocodePostcode4Prefix(postcodeQuery.prefix);
    } else if (postcodeQuery) {
      coords = await fetchPdokCoords(normalizedQuery, {
        postcodeType: true,
        expectedPrefix: postcodeQuery.prefix.slice(0, 4),
      });
    } else {
      coords = await fetchPdokCoords(normalizedQuery, { postcodeType: false });
    }

    if (!coords) return null;

    state.searchGeocodeCache.set(cacheKey, coords);
    return coords;
  } catch (_error) {
    return null;
  }
}

function renderStoreListOptions(stores, selectedStoreId) {
  return stores
    .map(
      (store) => `
        <button
          type="button"
          class="sample-request__store-option${store.id === selectedStoreId ? ' is-active' : ''}"
          data-store-option="${store.id}"
        >
          <span class="sample-request__store-option-name">${store.city}</span>
          <span class="sample-request__store-option-address">${store.street}</span>
          <span class="sample-request__store-option-postal">${store.postalCode} ${store.city}</span>
        </button>
      `
    )
    .join('');
}

function renderStoreListWithMessage(list, message, stores, selectedStoreId) {
  list.innerHTML = '';

  const status = document.createElement('p');
  status.className = 'sample-request__store-search-status';
  status.textContent = message;
  list.append(status);

  list.insertAdjacentHTML('beforeend', renderStoreListOptions(stores, selectedStoreId));
}

async function populateStoreList(ttlMs) {
  const wrapper = getMainModal();
  if (!wrapper) return;

  const list = wrapper.querySelector('[data-sample-request-store-list]');
  const searchInput = wrapper.querySelector('[data-sample-request-store-search]');
  if (!list) return;

  const rawTerm = (searchInput?.value || '').trim();
  const term = rawTerm.toLowerCase();
  const currentStore = getSummaryStore(ttlMs);
  const fallbackStoreId = state.activeStoreId || currentStore?.id || state.stores[0]?.id;
  const requestId = ++state.storeSearchRequestId;

  if (!term) {
    state.activeStoreId = fallbackStoreId;
    list.innerHTML = renderStoreListOptions(state.stores, state.activeStoreId);
    updateStoreDetail(ttlMs);
    return;
  }

  const localMatches = getLocalStoreMatches(state.stores, rawTerm);
  const willGeocode = shouldGeocodeSearch(rawTerm);

  if (willGeocode) {
    list.innerHTML = '<p class="sample-request__store-search-status">Zoeken...</p>';
  }

  const searchOrigin = await resolveSearchOrigin(rawTerm, localMatches);

  if (requestId !== state.storeSearchRequestId) return;

  if (searchOrigin) {
    const storesToShow = sortStoresByDistance(state.stores, searchOrigin);
    state.activeStoreId = storesToShow[0]?.id || fallbackStoreId;
    list.innerHTML = renderStoreListOptions(storesToShow, state.activeStoreId);
    updateStoreDetail(ttlMs);
    return;
  }

  const statusMessage =
    rawTerm.length < STORE_SEARCH_MIN_PLACE_LENGTH && !getPostcodeDigits(rawTerm)
      ? 'Typ minimaal 3 letters of 4 cijfers van een postcode.'
      : `Geen vestiging gevonden voor "${rawTerm}". Probeer een plaatsnaam of postcode (bijv. 7462BA).`;

  state.activeStoreId = fallbackStoreId;
  renderStoreListWithMessage(list, statusMessage, state.stores, state.activeStoreId);
  updateStoreDetail(ttlMs);
}

function schedulePopulateStoreList(ttlMs) {
  window.clearTimeout(state.storeSearchDebounceTimer);
  state.storeSearchDebounceTimer = window.setTimeout(() => {
    populateStoreList(ttlMs);
  }, STORE_SEARCH_DEBOUNCE_MS);
}

function updateStoreDetail(ttlMs) {
  const wrapper = getMainModal();
  if (!wrapper) return;

  const store = getSummaryStore(ttlMs);
  if (!store) return;

  const images = wrapper.querySelectorAll('[data-sample-request-store-image]');
  const name = wrapper.querySelector('[data-sample-request-store-name]');
  const address = wrapper.querySelector('[data-sample-request-store-address]');
  const phone = wrapper.querySelector('[data-sample-request-store-phone]');
  const email = wrapper.querySelector('[data-sample-request-store-email]');
  const phoneText = wrapper.querySelector('[data-sample-request-store-phone-text]');
  const emailText = wrapper.querySelector('[data-sample-request-store-email-text]');
  const weekdays = wrapper.querySelector('[data-sample-request-store-weekdays]');
  const saturday = wrapper.querySelector('[data-sample-request-store-saturday]');
  const sunday = wrapper.querySelector('[data-sample-request-store-sunday]');
  const confirmButton = wrapper.querySelector('[data-sample-request-confirm-store]');
  const summaryBlocks = wrapper.querySelectorAll('[data-sample-request-summary-store]');

  images.forEach((image) => {
    if (store.imageUrl) {
      image.src = store.imageUrl;
      image.hidden = false;
    } else {
      image.hidden = true;
    }
  });

  if (name) name.textContent = store.name;
  if (address) address.textContent = `${store.street}, ${store.postalCode} ${store.city}`;
  if (phone) {
    phone.href = `tel:${store.phone.replace(/\s+/g, '')}`;
  }
  if (phoneText) phoneText.textContent = store.phone;
  if (email) {
    email.href = `mailto:${store.email}`;
  }
  if (emailText) emailText.textContent = store.email;
  if (weekdays) weekdays.textContent = store.openingHours?.weekdays || '';
  if (saturday) saturday.textContent = store.openingHours?.saturday || '';
  if (sunday) sunday.textContent = store.openingHours?.sunday || '';
  if (confirmButton) confirmButton.disabled = false;

  summaryBlocks.forEach((block) => {
    const nameEl = block.querySelector('[data-sample-request-summary-store-name]');
    const addressEl = block.querySelector('[data-sample-request-summary-store-address]');
    const weekdaysEl = block.querySelector('[data-sample-request-summary-store-weekdays]');
    const saturdayEl = block.querySelector('[data-sample-request-summary-store-saturday]');
    const sundayEl = block.querySelector('[data-sample-request-summary-store-sunday]');

    if (nameEl) nameEl.textContent = store.name;
    if (addressEl) addressEl.textContent = `${store.street}, ${store.postalCode} ${store.city}`;
    if (weekdaysEl) weekdaysEl.textContent = store.openingHours?.weekdays || '';
    if (saturdayEl) saturdayEl.textContent = store.openingHours?.saturday || '';
    if (sundayEl) sundayEl.textContent = store.openingHours?.sunday || '';
  });
}

function updateSidebar(ttlMs) {
  const wrapper = getMainModal();
  if (!wrapper) return;

  const activeState = getActiveState(ttlMs);
  const samples = activeState?.samples || [];
  const maxSamples = getConfig(ttlMs).maxSamplesTotal;

  wrapper.querySelectorAll('[data-sample-request-sample-list]').forEach((list) => {
    list.innerHTML = renderSampleItems(samples, {
      allowRemove: true,
      maxSamplesTotal: maxSamples,
      showAddMore: list.dataset.showAddMore !== 'false',
    });
  });

  wrapper.querySelectorAll('[data-sample-request-sample-count]').forEach((countEl) => {
    countEl.textContent = `${samples.length}/${maxSamples}`;
  });

  updateBadge(ttlMs);
  updateRoots(ttlMs);
}

function fillForm(ttlMs) {
  const wrapper = getMainModal();
  const form = wrapper?.querySelector('[data-sample-request-form]');
  if (!form) return;

  const activeState = getActiveState(ttlMs);
  const customer = activeState?.customer;
  const store = getSummaryStore(ttlMs);
  const config = getConfig(ttlMs);

  const nameInput = form.querySelector('[name="customer_name"]');
  const emailInput = form.querySelector('[name="customer_email"]');
  const phoneInput = form.querySelector('[name="customer_phone"]');
  const privacyLink = wrapper.querySelector('[data-sample-request-privacy-link]');

  if (customer) {
    if (nameInput && !nameInput.value) nameInput.value = customer.name || '';
    if (emailInput && !emailInput.value) emailInput.value = customer.email || '';
    if (phoneInput && !phoneInput.value) phoneInput.value = customer.phone || '';
  }

  if (privacyLink) privacyLink.href = config.privacyUrl;
  if (store && !state.activeStoreId) state.activeStoreId = store.id;
}

function setModalStep(step) {
  const wrapper = getMainModal();
  if (!wrapper) return;

  wrapper.dataset.step = String(step);

  wrapper.querySelectorAll('[data-sample-request-step-panel]').forEach((panel) => {
    panel.hidden = panel.dataset.sampleRequestStepPanel !== String(step);
  });

  wrapper.querySelectorAll('[data-sample-request-step-indicator]').forEach((indicator) => {
    const indicatorStep = Number(indicator.dataset.sampleRequestStepIndicator);
    indicator.classList.toggle('is-active', indicatorStep <= step);
  });

  const backButton = wrapper.querySelector('[data-sample-request-back]');
  if (backButton) backButton.hidden = step === 1;

  const stepLabel = wrapper.querySelector('[data-sample-request-step-label]');
  const title = wrapper.querySelector('[data-sample-request-title]');
  if (stepLabel) stepLabel.textContent = `Stap ${step} van 2`;
  if (title) {
    title.textContent = step === 1 ? 'Kies een afhaallocatie' : 'Vul je gegevens in';
  }
}

function openMainModal({ step = 1, provisionalProductUrl = null, ttlMs = getDefaultTtlMs() } = {}) {
  const wrapper = getMainModal();
  const controller = getModalDialogController(wrapper);
  if (!wrapper || !controller) return;

  const activeState = getActiveState(ttlMs);
  if (!activeState?.samples?.length) return;

  const initialStep = step === 2 && !activeState?.store ? 1 : step;
  state.provisionalProductUrl = provisionalProductUrl;
  state.activeStoreId = activeState?.store?.id || state.activeStoreId || state.stores[0]?.id || null;

  populateStoreList(ttlMs);
  updateSidebar(ttlMs);
  renderOfferteRows(ttlMs, { force: true });
  fillForm(ttlMs);
  setModalStep(initialStep);
  controller.showDialog();
}

function closeMainModal() {
  const controller = getModalDialogController(getMainModal());
  controller?.closeDialog();
}

function openQuickDialog(sample, ttlMs) {
  const wrapper = getQuickDialog();
  const controller = getModalDialogController(wrapper);
  if (!wrapper || !controller) return;

  const count = getCount(ttlMs);
  const activeState = getActiveState(ttlMs);
  const store = activeState?.store || getSummaryStore(ttlMs);

  const title = wrapper.querySelector('[data-sample-request-quick-title]');
  const image = wrapper.querySelector('[data-sample-request-quick-image]');
  const storeEl = wrapper.querySelector('[data-sample-request-quick-store]');
  const countEl = wrapper.querySelector('[data-sample-request-quick-count]');

  if (title) title.textContent = sample.productTitle;
  if (image) {
    if (sample.imageUrl) {
      image.src = sample.imageUrl;
      image.hidden = false;
    } else {
      image.hidden = true;
    }
  }
  if (storeEl) storeEl.textContent = store?.name || '';
  if (countEl) countEl.textContent = `${count}/${getConfig(ttlMs).maxSamplesTotal}`;

  state.latestAddedProductUrl = sample.productUrl;
  controller.showDialog();
}

function closeQuickDialog() {
  const controller = getModalDialogController(getQuickDialog());
  controller?.closeDialog();
}

function buildSubmissionFingerprint(activeState) {
  const sampleUrls = activeState.samples
    .map((sample) => sample.productUrl)
    .sort()
    .join('|');

  return [
    activeState.customer.email?.trim().toLowerCase() || '',
    activeState.customer.phone?.trim() || '',
    activeState.store.id || '',
    sampleUrls,
  ].join('::');
}

function createIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `sr-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function getSubmissionIdempotencyKey(activeState) {
  const storage = window.sessionStorage;
  const fingerprint = buildSubmissionFingerprint(activeState);

  if (!storage) return createIdempotencyKey();

  const existingKey = storage.getItem(IDEMPOTENCY_STORAGE_KEY);
  const existingFingerprint = storage.getItem(IDEMPOTENCY_FP_STORAGE_KEY);

  if (existingKey && existingFingerprint === fingerprint) {
    return existingKey;
  }

  const key = createIdempotencyKey();
  storage.setItem(IDEMPOTENCY_STORAGE_KEY, key);
  storage.setItem(IDEMPOTENCY_FP_STORAGE_KEY, fingerprint);
  return key;
}

function clearSubmissionIdempotencyKey() {
  window.sessionStorage?.removeItem(IDEMPOTENCY_STORAGE_KEY);
  window.sessionStorage?.removeItem(IDEMPOTENCY_FP_STORAGE_KEY);
}

function createRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `req-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function ensureRequestId(ttlMs) {
  const activeState = getActiveState(ttlMs);
  if (activeState?.requestId) return;
  setRequestId(createRequestId(), { ttlMs });
}

// Zelfde formule als de PDP m²-calculator: dozen = ceil(m² * (1 + snijverlies/100) / m²PerDoos).
function computeDozen(m2, snijverlies, m2PerDoos) {
  if (!Number.isFinite(m2) || m2 <= 0) return null;
  if (!Number.isFinite(m2PerDoos) || m2PerDoos <= 0) return null;

  const wastage = Number.isFinite(snijverlies) ? snijverlies : 0;
  return Math.ceil((m2 * (1 + wastage / 100)) / m2PerDoos);
}

function isDozenComputed(sample) {
  return Number.isFinite(sample.dozen) && sample.dozen > 0;
}

function formatDozenText(sample) {
  if (isDozenComputed(sample)) {
    return `${sample.dozen} ${sample.dozen === 1 ? 'doos' : 'dozen'} nodig`;
  }

  if (!Number.isFinite(sample.m2PerDoos) || sample.m2PerDoos <= 0) {
    return 'Aantal dozen wordt handmatig bepaald.';
  }

  return 'Vul je m² in voor het aantal dozen.';
}

function getOfferteContainer() {
  return getMainModal()?.querySelector('[data-sample-request-offerte]') || null;
}

// Bouwt de offerte-regels alleen opnieuw als de set samples wijzigt, zodat focus
// in de invoervelden bewaard blijft tijdens het typen.
function renderOfferteRows(ttlMs, { force = false } = {}) {
  const container = getOfferteContainer();
  const rowsEl = container?.querySelector('[data-sample-request-offerte-rows]');
  if (!rowsEl) return;

  const samples = getActiveState(ttlMs)?.samples || [];
  const signature = samples.map((sample) => sample.productUrl).join('|');

  if (!force && signature === state.offerteRowsSignature) return;
  state.offerteRowsSignature = signature;

  rowsEl.innerHTML = samples
    .map((sample) => {
      const snijverlies = Number.isFinite(sample.snijverlies) ? sample.snijverlies : OFFERTE_DEFAULT_WASTAGE;
      const m2Value = Number.isFinite(sample.m2) ? sample.m2 : '';
      const key = encodeURIComponent(sample.productUrl);

      const resultMutedClass = isDozenComputed(sample) ? '' : ' sample-request-offerte__row-result--muted';

      return `
        <li class="sample-request-offerte__row" data-offerte-row="${key}">
          <p class="sample-request-offerte__row-title">${escapeHtml(sample.productTitle)}</p>
          <div class="sample-request-offerte__row-fields">
            <label class="sample-request-offerte__field">
              <span>Aantal m²</span>
              <span class="sample-request-offerte__input-wrap">
                <input type="number" min="0" step="0.01" inputmode="decimal" value="${m2Value}" placeholder="0" data-offerte-m2>
                <span class="sample-request-offerte__input-suffix">m²</span>
              </span>
            </label>
            <label class="sample-request-offerte__field">
              <span>Snijverlies</span>
              <span class="sample-request-offerte__input-wrap">
                <input type="number" min="0" max="100" step="0.1" inputmode="decimal" value="${snijverlies}" data-offerte-snijverlies>
                <span class="sample-request-offerte__input-suffix">%</span>
              </span>
            </label>
          </div>
          <p class="sample-request-offerte__row-result${resultMutedClass}" data-offerte-result>${formatDozenText(sample)}</p>
        </li>
      `;
    })
    .join('');
}

// Bij aangevinkte offerte-checkbox moeten m² en snijverlies voor elke tegel gevuld zijn.
function validateOfferte(wrapper) {
  const container = wrapper.querySelector('[data-sample-request-offerte]');
  const toggle = container?.querySelector('[data-sample-request-offerte-toggle]');
  if (!container || !toggle?.checked) return true;

  let valid = true;

  container.querySelectorAll('[data-offerte-row]').forEach((row) => {
    const m2Input = row.querySelector('[data-offerte-m2]');
    const snijverliesInput = row.querySelector('[data-offerte-snijverlies]');

    const m2Value = m2Input ? Number.parseFloat(m2Input.value) : NaN;
    const m2Valid = m2Input?.value !== '' && Number.isFinite(m2Value) && m2Value > 0;

    const snijverliesValue = snijverliesInput ? Number.parseFloat(snijverliesInput.value) : NaN;
    const snijverliesValid =
      snijverliesInput?.value !== '' &&
      Number.isFinite(snijverliesValue) &&
      snijverliesValue >= 0 &&
      snijverliesValue <= 100;

    if (m2Input) m2Input.classList.toggle('sample-request-offerte__field-input--error', !m2Valid);
    if (snijverliesInput) {
      snijverliesInput.classList.toggle('sample-request-offerte__field-input--error', !snijverliesValid);
    }

    if (!m2Valid || !snijverliesValid) valid = false;
  });

  return valid;
}

function handleOfferteInput(row, ttlMs) {
  const productUrl = decodeURIComponent(row.dataset.offerteRow || '');
  if (!productUrl) return;

  const sample = (getActiveState(ttlMs)?.samples || []).find((item) => item.productUrl === productUrl);
  if (!sample) return;

  const m2Input = row.querySelector('[data-offerte-m2]');
  const snijverliesInput = row.querySelector('[data-offerte-snijverlies]');
  const resultEl = row.querySelector('[data-offerte-result]');

  m2Input?.classList.remove('sample-request-offerte__field-input--error');
  snijverliesInput?.classList.remove('sample-request-offerte__field-input--error');

  const m2Parsed = m2Input && m2Input.value !== '' ? Number.parseFloat(m2Input.value) : null;
  const m2 = Number.isFinite(m2Parsed) ? m2Parsed : null;

  const snijverliesParsed =
    snijverliesInput && snijverliesInput.value !== ''
      ? Number.parseFloat(snijverliesInput.value)
      : OFFERTE_DEFAULT_WASTAGE;
  let snijverlies = Number.isFinite(snijverliesParsed) ? snijverliesParsed : OFFERTE_DEFAULT_WASTAGE;
  // Snijverlies hard begrenzen op 0–100% (voorkomt absurde dozen/quantity).
  snijverlies = Math.min(100, Math.max(0, snijverlies));
  if (snijverliesInput && Number.parseFloat(snijverliesInput.value) > 100) {
    snijverliesInput.value = '100';
  }

  const dozen = computeDozen(m2, snijverlies, sample.m2PerDoos);

  ensureRequestId(ttlMs);
  updateSample(productUrl, { m2, snijverlies, dozen }, { ttlMs });

  if (resultEl) {
    const updatedSample = { ...sample, m2, snijverlies, dozen };
    resultEl.textContent = formatDozenText(updatedSample);
    resultEl.classList.toggle('sample-request-offerte__row-result--muted', !isDozenComputed(updatedSample));
  }
}

// Uitvinken van de offerte-checkbox wist de ingevulde m²/snijverlies/dozen per
// tegel. Zonder dit blijft de data hangen: je krijgt dan alsnog een offerte met
// oude waarden, en bij heropenen staan ze er weer in.
function clearOfferteValues(ttlMs) {
  const samples = getActiveState(ttlMs)?.samples || [];
  samples.forEach((sample) => {
    updateSample(sample.productUrl, { m2: null, snijverlies: null, dozen: null }, { ttlMs });
  });
  state.offerteRowsSignature = null;
}

async function submitRequest(ttlMs) {
  if (state.submissionInFlight) return;

  const activeState = getActiveState(ttlMs);
  const wrapper = getMainModal();
  const formError = wrapper?.querySelector('[data-sample-request-form-error]');

  if (!activeState?.samples?.length || !activeState?.store || !activeState?.customer) {
    if (formError) {
      formError.hidden = false;
      formError.textContent = 'Vul eerst je vestiging en gegevens in.';
    }
    return;
  }

  const { submissionUrl, thankYouUrl } = getConfig(ttlMs);

  if (!submissionUrl) {
    if (formError) {
      formError.hidden = false;
      formError.textContent = 'Er ontbreekt een submission endpoint voor sample-aanvragen.';
    }
    return;
  }

  if (formError) {
    formError.hidden = true;
    formError.textContent = '';
  }

  if (!activeState.store.email) {
    if (formError) {
      formError.hidden = false;
      formError.textContent = 'Voor deze vestiging is nog geen ontvangend e-mailadres ingesteld.';
    }
    return;
  }

  const payload = new FormData();
  payload.append('customer_name', activeState.customer.name || '');
  payload.append('customer_email', activeState.customer.email || '');
  payload.append('customer_phone', activeState.customer.phone || '');
  payload.append('loan_agreement', 'accepted');
  payload.append('recipient_email', activeState.store.email || '');
  payload.append('samples_count', String(activeState.samples.length));
  payload.append(
    'samples_titles',
    activeState.samples.map((sample) => sample.productTitle).join(', ')
  );

  const idempotencyKey = getSubmissionIdempotencyKey(activeState);
  payload.append('idempotency_key', idempotencyKey);

  // Optionele offerte-bijvangst. Alleen meesturen als de offerte-checkbox áán
  // staat; anders sturen we geen m²/snijverlies/dozen mee, ook al staat er nog
  // iets in de (verborgen) state.
  const offerteToggle = wrapper?.querySelector('[data-sample-request-offerte-toggle]');
  const offerteRequested = !!offerteToggle?.checked;
  const offerteSamples = activeState.samples.map((sample) => ({
    variantId: sample.variantId || null,
    title: sample.productTitle || '',
    sku: sample.sku || '',
    imageUrl: sample.imageUrl || '',
    m2: offerteRequested && Number.isFinite(sample.m2) ? sample.m2 : null,
    snijverlies: offerteRequested && Number.isFinite(sample.snijverlies) ? sample.snijverlies : null,
    m2PerDoos: Number.isFinite(sample.m2PerDoos) ? sample.m2PerDoos : null,
    dozen: offerteRequested && Number.isFinite(sample.dozen) ? sample.dozen : null,
  }));
  const wantsOfferte = offerteRequested && offerteSamples.some((sample) => Number.isFinite(sample.m2) && sample.m2 > 0);

  payload.append('request_id', activeState.requestId || '');
  payload.append('wants_quotation', String(wantsOfferte));
  payload.append('preferred_location', activeState.store.city || activeState.store.name || '');
  payload.append('samples_json', JSON.stringify(offerteSamples));

  const submitButtons = document.querySelectorAll('[data-sample-request-submit]');
  submitButtons.forEach((button) => {
    button.disabled = true;
  });

  state.submissionInFlight = true;

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), SUBMISSION_TIMEOUT_MS);

  try {
    const response = await fetch(submissionUrl, {
      method: 'POST',
      headers: {
        'Idempotency-Key': idempotencyKey,
      },
      body: payload,
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    clearSubmissionIdempotencyKey();
    setSubmittedRequestData(activeState);
    clearState();
    window.location.href = thankYouUrl;
  } catch (error) {
    if (formError) {
      formError.hidden = false;
      formError.textContent =
        error?.name === 'AbortError'
          ? 'Het antwoord duurde te lang. We weten niet zeker of je aanvraag is ontvangen. Controleer je e-mail; je kunt het veilig opnieuw proberen met dezelfde gegevens.'
          : 'Er ging iets mis bij het versturen. Je kunt het opnieuw proberen; dubbele aanvragen worden voorkomen zolang je gegevens ongewijzigd blijven.';
    }
  } finally {
    state.submissionInFlight = false;
    window.clearTimeout(timeoutId);
    submitButtons.forEach((button) => {
      button.disabled = false;
    });
  }
}

function attachGlobalModalEvents(ttlMs) {
  const wrapper = getMainModal();
  if (!wrapper || wrapper.dataset.initialized === 'true') return;

  wrapper.dataset.initialized = 'true';
  patchModalCloseGuard(wrapper);

  const searchInput = wrapper.querySelector('[data-sample-request-store-search]');
  const form = wrapper.querySelector('[data-sample-request-form]');
  const offerteContainer = wrapper.querySelector('[data-sample-request-offerte]');
  const offerteToggle = wrapper.querySelector('[data-sample-request-offerte-toggle]');
  const offerteBody = wrapper.querySelector('[data-sample-request-offerte-body]');

  searchInput?.addEventListener('input', () => {
    schedulePopulateStoreList(ttlMs);
  });

  offerteToggle?.addEventListener('change', () => {
    const willOpen = offerteToggle.checked;
    if (offerteBody) offerteBody.hidden = !willOpen;

    if (willOpen) {
      ensureRequestId(ttlMs);
      renderOfferteRows(ttlMs, { force: true });
    } else {
      clearOfferteValues(ttlMs);
    }
  });

  offerteContainer?.addEventListener('input', (event) => {
    const row = event.target.closest('[data-offerte-row]');
    if (row) handleOfferteInput(row, ttlMs);
  });

  wrapper.addEventListener(
    'mousedown',
    (event) => {
      if (!event.target.closest('[data-remove-sample]')) return;

      state.preventModalClose = true;
      window.setTimeout(() => {
        state.preventModalClose = false;
      }, 400);
    },
    true
  );

  wrapper.addEventListener('click', (event) => {
    if (handleSampleRemove(event, ttlMs)) return;

    const storeButton = event.target.closest('[data-store-option]');
    const closeButton = event.target.closest('[data-sample-request-close]');
    const addMoreButton = event.target.closest('[data-sample-request-add-more]');

    if (closeButton) {
      closeMainModal();
      return;
    }

    if (storeButton) {
      state.activeStoreId = storeButton.dataset.storeOption;
      // Defer the rerender so the dialog's outside-click handler
      // still sees the original clicked element inside the modal.
      requestAnimationFrame(() => {
        populateStoreList(ttlMs);
      });
      return;
    }

    if (addMoreButton) {
      const href = addMoreButton.getAttribute('href');
      state.provisionalProductUrl = null;
      closeMainModal();
      if (href) {
        window.location.assign(href);
      }
    }
  });

  wrapper.querySelector('[data-sample-request-confirm-store]')?.addEventListener('click', () => {
    const selectedStore = getSummaryStore(ttlMs);
    if (!selectedStore) return;
    setStore(selectedStore, { ttlMs });
    fillForm(ttlMs);
    updateSidebar(ttlMs);
    setModalStep(2);
  });

  wrapper.querySelector('[data-sample-request-back]')?.addEventListener('click', () => {
    if (wrapper.dataset.step === '1') {
      closeMainModal();
      return;
    }

    setModalStep(1);
  });

  form?.addEventListener('submit', async (event) => {
    event.preventDefault();

    if (state.submissionInFlight) return;

    const loanAgreementAccepted = Boolean(
      form.querySelector('[name="loan_agreement"]')?.checked
    );
    const formError = wrapper.querySelector('[data-sample-request-form-error]');

    if (!loanAgreementAccepted) {
      if (formError) {
        formError.hidden = false;
        formError.textContent = 'Bevestig dat je de sample in bruikleen terugbrengt.';
      }
      return;
    }

    if (!validateOfferte(wrapper)) {
      if (formError) {
        formError.hidden = false;
        formError.textContent =
          'Vul voor elke tegel de m² en het snijverlies in, of vink de offerte uit.';
      }
      return;
    }

    if (formError) {
      formError.hidden = true;
      formError.textContent = '';
    }

    const selectedStore = getSummaryStore(ttlMs);
    const customer = {
      name: form.querySelector('[name="customer_name"]')?.value.trim() || '',
      email: form.querySelector('[name="customer_email"]')?.value.trim() || '',
      phone: form.querySelector('[name="customer_phone"]')?.value.trim() || '',
    };

    if (!selectedStore) {
      return;
    }

    setStore(selectedStore, { ttlMs });
    setCustomer(customer, { ttlMs });
    state.provisionalProductUrl = null;
    await submitRequest(ttlMs);
  });

  wrapper.addEventListener('close', () => {
    const activeState = getActiveState(ttlMs);

    if (state.provisionalProductUrl && (!activeState?.customer || !activeState?.store)) {
      removeSample(state.provisionalProductUrl, { ttlMs });
    }

    state.provisionalProductUrl = null;
  });
}

function attachQuickDialogEvents(ttlMs) {
  const wrapper = getQuickDialog();
  if (!wrapper || wrapper.dataset.initialized === 'true') return;

  wrapper.dataset.initialized = 'true';

  wrapper.addEventListener('click', (event) => {
    const closeButton = event.target.closest('[data-sample-request-close]');
    if (closeButton) {
      closeQuickDialog();
    }
  });

  wrapper.querySelector('[data-sample-request-quick-continue]')?.addEventListener('click', () => {
    closeQuickDialog();
  });

  wrapper.querySelector('[data-sample-request-quick-review]')?.addEventListener('click', async () => {
    closeQuickDialog();
    openMainModal({ step: 2, ttlMs });
  });
}

function initRoots(ttlMs) {
  document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const button = root.querySelector('[data-sample-request-trigger]');
    const errorEl = root.querySelector('[data-sample-request-error]');

    button?.addEventListener('click', () => {
      const config = normalizeConfigFromDataset(root.dataset);
      const m2PerDoosRaw = root.dataset.productM2PerDoos
        ? Number.parseFloat(root.dataset.productM2PerDoos)
        : null;
      const sample = {
        productTitle: root.dataset.productTitle || '',
        productType: root.dataset.productType || '',
        sku: root.dataset.productSku || '',
        variantId: root.dataset.productVariantId || '',
        m2PerDoos: Number.isFinite(m2PerDoosRaw) ? m2PerDoosRaw : null,
        price: root.dataset.productPrice || '',
        priceUnit: root.dataset.productPriceUnit || '',
        imageUrl: root.dataset.productImage || '',
        productUrl: root.dataset.productUrl || '',
      };

      setSettings(config, { ttlMs });

      if (hasProduct(sample.productUrl, ttlMs)) {
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent = root.dataset.labelAlreadyRequested;
        }
        updateRoots(ttlMs);
        return;
      }

      const currentCount = getCount(ttlMs);
      if (currentCount >= config.maxSamplesTotal) {
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent = root.dataset.labelMaxReachedMessage;
        }
        updateRoots(ttlMs);
        return;
      }

      const activeState = getActiveState(ttlMs);
      const result = addSample(sample, { ttlMs });
      if (!result.added) return;

      updateSidebar(ttlMs);
      populateStoreList(ttlMs);

      if (!activeState?.store || !activeState?.customer) {
        openMainModal({ step: 1, provisionalProductUrl: sample.productUrl, ttlMs });
      } else {
        openQuickDialog(sample, ttlMs);
      }
    });
  });
}

function initBadge(ttlMs) {
  const badges = getBadges();
  if (!badges.length) return;

  badges.forEach((badge) => {
    if (badge.dataset.initialized === 'true') return;

    badge.dataset.initialized = 'true';
    badge.addEventListener('click', () => {
      openMainModal({ step: 1, ttlMs });
    });
  });
}

function hasSampleBadgeInNode(node) {
  if (!(node instanceof Element)) return false;
  return node.matches(BADGE_SELECTOR) || Boolean(node.querySelector(BADGE_SELECTOR));
}

function isBadgeOutOfSync(ttlMs) {
  const expected = String(getCount(ttlMs));
  const expectHidden = expected === '0';

  return getBadges().some((badge) => {
    if (badge.hidden !== expectHidden) return true;
    const countEl = badge.querySelector('[data-sample-request-count]');
    return Boolean(countEl) && countEl.textContent !== expected;
  });
}

function observeBadgeMounts(ttlMs) {
  if (state.badgeObserver || typeof MutationObserver === 'undefined' || !document.body) return;

  state.badgeObserver = new MutationObserver((mutations) => {
    let needsResync = false;

    for (const mutation of mutations) {
      if (
        mutation.type === 'childList' &&
        Array.from(mutation.addedNodes).some((node) => hasSampleBadgeInNode(node))
      ) {
        needsResync = true;
        break;
      }

      const target = mutation.target;
      const targetEl = target instanceof Element ? target : target?.parentElement || null;
      if (targetEl?.closest?.(BADGE_SELECTOR)) {
        needsResync = true;
        break;
      }
    }

    // Skip if nothing in the badges changed, or if our state already matches
    // the DOM (prevents loops from our own updates).
    if (!needsResync || !isBadgeOutOfSync(ttlMs)) return;

    initBadge(ttlMs);
    updateBadge(ttlMs);
  });

  state.badgeObserver.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
  });
}

function bindCartEventListeners(ttlMs) {
  // The theme morphs the header in place on cart updates, which resets the
  // sample badge to its initial Liquid markup. Re-sync after the morph runs.
  const resync = () => {
    requestAnimationFrame(() => {
      initBadge(ttlMs);
      updateBadge(ttlMs);
    });
  };

  document.addEventListener('cart:update', resync);
  document.addEventListener('cart:error', resync);
}

function initThankYou(ttlMs) {
  document.querySelectorAll(THANK_YOU_SELECTOR).forEach((container) => {
    if (container.dataset.initialized === 'true') return;
    container.dataset.initialized = 'true';

    let submittedData = null;
    try {
      const submittedDataRaw = window.sessionStorage?.getItem('tms_sample_request_submitted');
      submittedData = submittedDataRaw ? JSON.parse(submittedDataRaw) : null;
    } catch (_error) {
      submittedData = null;
    }

    if (!submittedData?.samples?.length) {
      container.hidden = true;
      return;
    }

    // Offerte-velden voor de dataLayer (geen PII): is er m² ingevuld, totaal m²
    // en het gekozen snijverlies. offerte_requested = minstens één regel met m².
    const offerteSamples = submittedData.samples.filter(
      (sample) => Number.isFinite(sample.m2) && sample.m2 > 0
    );
    const offerteRequested = offerteSamples.length > 0;
    const offerteM2 = offerteRequested
      ? Math.round(offerteSamples.reduce((sum, sample) => sum + sample.m2, 0) * 100) / 100
      : null;
    const offerteSnijverlies =
      offerteRequested && Number.isFinite(offerteSamples[0].snijverlies)
        ? offerteSamples[0].snijverlies
        : null;

    // Push customer details to the dataLayer for GTM enhanced conversions.
    // Plain text by design — GTM hashes before forwarding to ad platforms.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'sample_request_submitted',
      form_name: 'sample_request',
      customer_name: submittedData.customer?.name || '',
      customer_email: submittedData.customer?.email || '',
      customer_phone: submittedData.customer?.phone || '',
      samples_count: submittedData.samples.length,
      offerte_requested: offerteRequested,
      offerte_m2: offerteM2,
      offerte_snijverlies_pct: offerteSnijverlies,
      loan_agreement_accepted: true,
      samples: submittedData.samples.map((sample) => ({
        title: sample.productTitle || '',
        sku: sample.sku || '',
        product_type: sample.productType || '',
        product_url: sample.productUrl || '',
      })),
    });

    const cards = container.querySelector('[data-sample-request-thank-you-cards]');
    const subtitle = container.querySelector('[data-sample-request-thank-you-subtitle]');
    const storeName = container.querySelector('[data-sample-request-thank-you-store-name]');
    const storeAddress = container.querySelector('[data-sample-request-thank-you-store-address]');
    const weekdays = container.querySelector('[data-sample-request-thank-you-weekdays]');
    const saturday = container.querySelector('[data-sample-request-thank-you-saturday]');
    const sunday = container.querySelector('[data-sample-request-thank-you-sunday]');
    const phone = container.querySelector('[data-sample-request-thank-you-phone]');

    const sampleCount = submittedData.samples.length;

    if (subtitle) {
      const lead =
        sampleCount === 1
          ? 'Je gratis sample is aangevraagd.'
          : `Je ${sampleCount} gratis samples zijn aangevraagd.`;
      subtitle.textContent = `${lead} De vestiging neemt contact met je op om de aanvraag te bevestigen.`;
    }

    if (cards) {
      cards.innerHTML = submittedData.samples
        .map((sample) => {
          const imageMarkup = sample.imageUrl
            ? `<img src="${sample.imageUrl}" alt="${sample.productTitle}" loading="lazy">`
            : '';

          const typeMarkup = sample.productType
            ? `<p class="sample-request-thank-you__sample-type">${sample.productType}</p>`
            : '';

          const priceMarkup = sample.price
            ? `<p class="sample-request-thank-you__sample-price"><strong>${sample.price}</strong>${
                sample.priceUnit
                  ? `<span class="sample-request-thank-you__sample-price-unit">${sample.priceUnit}</span>`
                  : ''
              }</p>`
            : '';

          return `
            <article class="sample-request-thank-you__sample">
              <div class="sample-request-thank-you__sample-image">${imageMarkup}</div>
              <div class="sample-request-thank-you__sample-content">
                ${typeMarkup}
                <h2 class="sample-request-thank-you__sample-title">${sample.productTitle}</h2>
                ${priceMarkup}
              </div>
            </article>
          `;
        })
        .join('');
    }

    if (storeName) storeName.textContent = submittedData.store?.name || '';
    if (storeAddress) {
      const street = submittedData.store?.street || '';
      const postalCode = submittedData.store?.postalCode || '';
      const city = submittedData.store?.city || '';
      const cityLine = `${postalCode} ${city}`.trim();
      storeAddress.textContent = [street, cityLine].filter(Boolean).join(', ');
    }
    if (weekdays) weekdays.textContent = submittedData.store?.openingHours?.weekdays || '';
    if (saturday) saturday.textContent = submittedData.store?.openingHours?.saturday || '';
    if (sunday) sunday.textContent = submittedData.store?.openingHours?.sunday || '';
    if (phone) {
      phone.textContent = submittedData.store?.phone || '';
      phone.href = `tel:${(submittedData.store?.phone || '').replace(/\s+/g, '')}`;
    }

    window.sessionStorage?.removeItem('tms_sample_request_submitted');
  });
}

function initSampleRequest() {
  const ttlMs = getDefaultTtlMs();
  state.stores = parseStoreLocations();
  attachGlobalModalEvents(ttlMs);
  attachQuickDialogEvents(ttlMs);
  initRoots(ttlMs);
  initBadge(ttlMs);
  observeBadgeMounts(ttlMs);
  bindCartEventListeners(ttlMs);
  initThankYou(ttlMs);
  updateSidebar(ttlMs);
  populateStoreList(ttlMs);

  // Safety net for themes that hydrate header badges a bit later.
  window.setTimeout(() => {
    initBadge(ttlMs);
    updateBadge(ttlMs);
  }, 300);
}

document.addEventListener('sample-request:updated', () => {
  const ttlMs = getDefaultTtlMs();
  updateSidebar(ttlMs);
  populateStoreList(ttlMs);
  renderOfferteRows(ttlMs);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSampleRequest, { once: true });
} else {
  initSampleRequest();
}
